import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Trash, User } from "lucide-react";
import api, { setupAxiosInterceptors } from "../axiosSetup";
import CustomNavbar from "../CustomNavbar";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button } from "react-bootstrap";
import "./css/Modal-Style.css"
import cfg from "../../config";

const ZRDownload = () => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const navigate = useNavigate();

  setupAxiosInterceptors(navigate);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get("/api/files", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFiles(response.data || []);
    } catch (error) {
      console.error("Fehler beim Laden der Dateien:", error);
      setMessage({ type: "danger", text: "Fehler beim Laden der Dateien." });
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!file) {
      setMessage({ type: "danger", text: "Bitte wähle eine Datei aus." });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("description", description);

    try {
      setUploading(true);
      const token = localStorage.getItem("authToken");
      const response = await api.post("/api/files/upload", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setMessage({
        type: "success",
        text: response.data?.message || "Datei erfolgreich hochgeladen.",
      });
      setFile(null);
      setDescription("");
      fetchFiles();
    } catch (error) {
      console.error(error);
      setMessage({
        type: "danger",
        text:
          error.response?.data?.message ||
          "Fehler beim Hochladen der Datei. Bitte versuche es erneut.",
      });
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = (fileItem) => {
    setFileToDelete(fileItem);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;

    try {
      const token = localStorage.getItem("authToken");
      await api.delete(`/api/files/${fileToDelete.filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMessage({ type: "success", text: "Datei erfolgreich gelöscht." });
      setShowDeleteModal(false);
      setFileToDelete(null);
      fetchFiles();
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
      setMessage({ type: "danger", text: "Fehler beim Löschen der Datei." });
      setShowDeleteModal(false);
      setFileToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <>
      <CustomNavbar />
      <div className="container mt-4 text-white">
        <h3 className="text-center mb-4">📁 Dateien verwalten</h3>
        <p style={{ color: "white" }}>
          Hier kannst du Dateien hochladen oder löschen, die später für {cfg.memberTermPlural} bereitstehen.
        </p>

        {message && (
          <div
            className={`alert alert-${message.type} mt-3`}
            role="alert"
            style={{
              color: "white",
              backgroundColor: message.type === "danger" ? "#dc3545" : "#198754",
            }}
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleUpload} className="mt-4">
          <div className="mb-3">
            <label htmlFor="description" className="form-label text-white">
              Name
            </label>
            <input
              type="text"
              id="description"
              className="form-control text-white bg-dark border-secondary"
              placeholder="angezeigter Name der Datei"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="file" className="form-label text-white">
              Datei auswählen
            </label>
            <input
              type="file"
              id="file"
              className="form-control text-white bg-dark border-secondary"
              onChange={handleFileChange}
              accept="*/*"
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={uploading}>
            {uploading ? "Wird hochgeladen..." : "Datei hochladen"}
          </button>
        </form>

        <hr className="my-4" />

        <div className="row g-3">
          {files.map((fileItem) => (
            <div key={fileItem.id} className="col-12 col-md-6 col-lg-4">
              <div
                className="file-card shadow-sm p-3 rounded-4 h-100 d-flex flex-column justify-content-between bg-dark"
                style={{ color: "white" }}
              >
                <div className="d-flex align-items-start gap-3">
                  <FileText size={36} className="text-primary" />
                  <div className="flex-grow-1">
                    <h5 className="fw-semibold mb-1" style={{ color: "white" }}>
                      {fileItem.description || "Ohne Titel"}
                    </h5>
                    <div style={{ color: "white" }}>{fileItem.originalname}</div>
                    <div className="mt-1 d-flex align-items-center gap-1" style={{ color: "white" }}>
                      hochgeladen von: <User size={14} /> {fileItem.uploader || "Unbekannt"}
                    </div>
                    <div style={{ color: "white" }}>
                      hochgeladen am: 📅 {formatDate(fileItem.upload_date)}
                    </div>
                    {fileItem.vorhanden === false && (
                      <div className="alert alert-warning mt-2 mb-0 py-2">
                        Die Datei fehlt auf dem Server und lässt sich nicht herunterladen.
                        Bitte lade sie erneut hoch und entferne diesen Eintrag.
                      </div>
                    )}
                  </div>
                </div>

                <button
                  className="btn btn-danger w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => confirmDelete(fileItem)}
                >
                  <Trash size={18} /> Löschen
                </button>
              </div>
            </div>
          ))}
        </div>

       {/* Delete Confirmation Modal (angepasst an deinen App-Stil) */}
      {showDeleteModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="modal-box animate-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* === Header === */}
            <div className="modal-header">
              <h5 className="modal-title text-dark">Datei löschen</h5>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                &times;
              </button>
            </div>

            {/* === Body === */}
            <div className="modal-body text-dark">
              <p>
                Möchtest du die Datei{" "}
                <strong>{fileToDelete?.description || fileToDelete?.originalname}</strong>{" "}
                {fileToDelete?.description && fileToDelete?.originalname
                  ? `(${fileToDelete.originalname}) `
                  : ""}
                wirklich löschen?
              </p>
            </div>

            {/* === Footer === */}
            <div className="modal-footer mt-4 d-flex flex-wrap justify-content-end gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Abbrechen
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </>
  );
};

export default ZRDownload;
